import React, { useState } from 'react';
import { Box, Button, H2, H3, Panel, Text, Form, FormGroup, Input, Checkbox, Modal, Flex, FlexItem } from '@bigcommerce/big-design';
import { StarIcon, EditIcon } from '@bigcommerce/big-design-icons';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { loadDefaultSettings, saveSettings } from '../settingsSlice';
import { useFormik } from 'formik';
import ReviewCriteriaManager from './ReviewCriteriaManager';
import { alertsManager } from '../App';

/**
 * Custom hook để navigation với signed_payload
 */
const useRouteTo = () => {
    const history = useHistory();
    const baseParams = new URLSearchParams(useLocation().search);

    return (params) => {
        const urlParams = new URLSearchParams(params);
        urlParams.set('signed_payload', baseParams.get('signed_payload'));
        history.push(`?${urlParams.toString()}`);
    }
};

/**
 * Component quản lý cài đặt ứng dụng
 */
const Settings = () => {
    const dispatch = useDispatch();
    const routeTo = useRouteTo();
    const { data: settings, defaults: defaultSettings } = useSelector(state => state.settings);
    const [isOpenCancelModal, setIsOpenCancelModal] = useState(false);

    const form = useFormik({
        initialValues: settings,
        enableReinitialize: true,
        onSubmit: values => {
            dispatch(saveSettings(values)).then(() => {
                form.setSubmitting(false);
                alertsManager.add({
                    header: 'Settings saved!',
                    messages: [{
                        text: 'Your settings have been saved successfully.'
                    }],
                    type: 'success',
                    autoDismiss: true,
                });
                routeTo({ route: '' });
            })
        },
    });

    const handleRestore = () => {
        form.setValues(defaultSettings);
    };

    const cancel = () => {
        routeTo({ route: '' });
    }

    return (
        <Form onSubmit={form.handleSubmit} fullWidth={true}>
            <Flex marginBottom="medium">
                <FlexItem flexGrow={1}>
                    <H2>Settings</H2>
                </FlexItem>
                <FlexItem>
                    <Button type="button" variant="secondary" onClick={() => setIsOpenCancelModal(true)}>Cancel</Button>
                    <Button type="submit" isLoading={form.isSubmitting}>Save</Button>
                </FlexItem>
            </Flex>
            <Panel>
                {/* restrict Purchaser Only */}
                <FormGroup>
                    <Checkbox
                        name="restrictPurchaserOnly"
                        label="Only accept product reviews from past customers"
                        description={`Accept reviews only from customers whose email matches that of the purchaser. Default: ${defaultSettings.restrictPurchaserOnly ? 'Yes' : 'No'}`}
                        checked={form.values.restrictPurchaserOnly}
                        onChange={form.handleChange}
                        error={form.touched.restrictPurchaserOnly && form.errors.restrictPurchaserOnly}
                    />
                </FormGroup>
            </Panel>
            {/* notification Email */}
            <Panel>
                <H3 bold>Notification Email</H3>
                <FormGroup>
                    <Checkbox
                        name="allowSendNotificationEmail"
                        label="Allow sending notification emails"
                        description={`Default: ${defaultSettings.allowSendNotificationEmail ? 'Yes' : 'No'}`}
                        checked={form.values.allowSendNotificationEmail}
                        onChange={form.handleChange}
                        error={form.touched.allowSendNotificationEmail && form.errors.allowSendNotificationEmail}
                    />
                </FormGroup>
                <FormGroup>
                    <Input
                        name='notificationEmail'
                        description="This email will receive a notification whenever a new product review is submitted. If left blank, notifications will be sent to the store owner's email by default."
                        label="Notification Email"
                        onChange={form.handleChange}
                        placeholder="Enter Notification Email"
                        type="email"
                        required
                        value={form.values.notificationEmail}
                        error={form.touched.notificationEmail && form.errors.notificationEmail}
                    />
                </FormGroup>
            </Panel>
            {/* Featured Reviews */}
            <Panel>
                <H3 bold>Featured Reviews</H3>
                <FormGroup>
                    <Input
                        name='featuredReviewsLimit'
                        description={`Limit the number of Featured Reviews shown. Maximum allowed is 100. Default: ${defaultSettings.featuredReviewsLimit}`}
                        label="Featured Reviews limit"
                        onChange={form.handleChange}
                        placeholder="Enter Featured Reviews limit"
                        type="number"
                        required
                        min={0}
                        max={100}
                        value={form.values.featuredReviewsLimit}
                        error={form.touched.featuredReviewsLimit && form.errors.featuredReviewsLimit}
                    />
                </FormGroup>
            </Panel>
            {/* Review Criteria Settings */}
            <Panel>
                <H3 bold><StarIcon color="warning" /> Extended Review Criteria</H3>
                <FormGroup>
                    <Checkbox
                        name="enableReviewCriteria"
                        label="Enable detailed review criteria feature"
                        description={`Allow customers to rate products with detailed criteria. Default: ${defaultSettings.enableReviewCriteria ? 'Enabled' : 'Disabled'}`}
                        checked={!!form.values.enableReviewCriteria}
                        onChange={(e) => form.setFieldValue('enableReviewCriteria', e.target.checked)}
                        error={form.touched.enableReviewCriteria && form.errors.enableReviewCriteria}
                    />
                </FormGroup>

                {form.values.enableReviewCriteria && (
                    <Box marginTop="medium">
                        <Text bold marginBottom="small"><EditIcon size="medium" /> Manage Review Criteria:</Text>
                        <ReviewCriteriaManager
                            criteria={form.values.reviewCriteria || defaultSettings.reviewCriteria || []}
                            onChange={(newCriteria) => form.setFieldValue('reviewCriteria', newCriteria)}
                        />
                    </Box>
                )}
            </Panel>
            {/* Action */}
            <Panel>
                <Box>
                    <Button type="button" variant="secondary" onClick={form.handleReset} disabled={form.isSubmitting}>Reset</Button>
                    <Button type="button" variant="subtle" onClick={handleRestore} disabled={form.isSubmitting}>Restore to default settings</Button>
                </Box>
            </Panel>
            <Modal
                header="Are you sure you want to leave?"
                actions={[
                    { text: 'Cancel', variant: 'subtle', onClick: () => setIsOpenCancelModal(false) },
                    { text: 'Yes, leave this page', onClick: () => { setIsOpenCancelModal(false); cancel(); } },
                ]}
                isOpen={isOpenCancelModal}
                onClose={() => setIsOpenCancelModal(false)}
            >
                <Text>You've made some changes. If you leave, they won't be saved.</Text>
            </Modal>
        </Form>
    );
}

export default Settings;
